import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'

import {
  isEventKeyEnter,
  isEventKeyEscape,
  isEventKeySpaceBar,
  preventDefault,
  stopPropagation,
} from '../../../../../utils/events'
import { PHASE_STRIP_SEARCH_PARAM } from '../../RentalPortalPhaseBar.constants'
import {
  getPhaseBarNextChevron,
  getPhaseChevronId,
} from '../../RentalPortalPhaseBar.utils'

import {
  checkHasPhaseStripStaleParam,
  getPhaseStripInitialPicked,
  getPhaseStripOpenKey,
  getPhaseStripSearchParams,
  getPhaseStripToggledKey,
} from './usePhaseStrip.utils'

export function usePhaseStrip({
  activePhaseKey,
  barClassName,
  baseId,
  isPreview,
  phases,
}) {
  const [searchParams, setSearchParams] = useSearchParams()

  const [pickedPhase, setPickedPhase] = useState(() =>
    getPhaseStripInitialPicked({ isPreview, searchParams })
  )
  const [prevActivePhaseKey, setPrevActivePhaseKey] = useState(activePhaseKey)

  const openPhaseKey = getPhaseStripOpenKey({
    activePhaseKey,
    phases,
    pickedPhase,
  })

  const paramKey = searchParams.get(PHASE_STRIP_SEARCH_PARAM)

  const [prevParamKey, setPrevParamKey] = useState(paramKey)

  if (paramKey !== prevParamKey) {
    setPrevParamKey(paramKey)

    if (!isPreview && paramKey !== openPhaseKey) {
      setPickedPhase({ key: paramKey })
    }
  }

  if (activePhaseKey !== prevActivePhaseKey) {
    setPrevActivePhaseKey(activePhaseKey)

    if (prevActivePhaseKey) {
      setPickedPhase(null)
    }
  }

  useEffect(() => {
    if (
      checkHasPhaseStripStaleParam({
        isPreview,
        openPhaseKey,
        paramKey,
        phases,
      })
    ) {
      setSearchParams(
        getPhaseStripSearchParams({
          phaseKey: openPhaseKey,
          searchParams,
        }),
        { replace: true }
      )
    }
  }, [openPhaseKey, phases])

  function handleTogglePhase(phaseKey) {
    const nextPhaseKey = getPhaseStripToggledKey({ openPhaseKey, phaseKey })

    setPickedPhase({ key: nextPhaseKey })

    if (!isPreview) {
      setSearchParams(
        getPhaseStripSearchParams({
          phaseKey: nextPhaseKey,
          searchParams,
        }),
        { replace: true }
      )
    }
  }

  function handleCollapseOpenPhase(event) {
    stopPropagation(event)

    document
      .getElementById(getPhaseChevronId({ baseId, phaseKey: openPhaseKey }))
      ?.focus()

    handleTogglePhase(openPhaseKey)
  }

  function handleKeyDownPhase(event) {
    const nextChevron = getPhaseBarNextChevron({
      barClassName,
      key: event.key,
      target: event.currentTarget,
    })

    if (nextChevron) {
      preventDefault(event)

      nextChevron.focus()
    } else if (isEventKeyEnter(event) || isEventKeySpaceBar(event)) {
      preventDefault(event)

      handleTogglePhase(event.currentTarget.dataset.phaseKey)
    } else if (isEventKeyEscape(event) && openPhaseKey) {
      handleCollapseOpenPhase(event)
    }
  }

  function handleKeyDownStrip(event) {
    if (isEventKeyEscape(event) && openPhaseKey) {
      handleCollapseOpenPhase(event)
    }
  }

  return {
    handleKeyDownPhase,
    handleKeyDownStrip,
    handleTogglePhase,
    openPhaseKey,
  }
}
